import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Button, TextInput as PaperTextInput, useTheme } from 'react-native-paper';
import BaseBottomSheet from './BaseBottomSheet';

type PaymentType = 'cash' | 'debt';

interface CompleteJobSheetProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (paymentType: PaymentType, serviceCost: number) => void;
}

const CurrencyIcon = () => <Text style={styles.currencyIcon}>UGX</Text>;

/**
 * Payment-type sheet used to complete an assigned job. Shared by the driver
 * board and a driving supervisor's Job Queue. Captures the payment type and an
 * optional service-delivery cost, and resets on close.
 */
const CompleteJobSheet: React.FC<CompleteJobSheetProps> = ({
  visible,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType | null>(null);
  const [serviceCost, setServiceCost] = useState('');

  const handleClose = () => {
    setSelectedPaymentType(null);
    setServiceCost('');
    onClose();
  };

  const parsedCost = parseFloat(serviceCost.trim());
  const isCostValid = Number.isFinite(parsedCost) && parsedCost >= 0;
  const canConfirm = !!selectedPaymentType && isCostValid;

  const handleConfirm = () => {
    if (!selectedPaymentType || !isCostValid) return;
    onConfirm(selectedPaymentType, parsedCost);
    setSelectedPaymentType(null);
    setServiceCost('');
  };

  return (
    <BaseBottomSheet
      visible={visible}
      onClose={handleClose}
      title="Complete Job"
      height={0.5}
    >
      <View style={styles.paymentContent}>
        <Text style={[styles.paymentLabel, { color: theme.colors.onSurface }]}>
          Select Payment Type
        </Text>
        <View style={styles.paymentRow}>
          <Button
            testID="payment-cash"
            mode={selectedPaymentType === 'cash' ? 'contained' : 'outlined'}
            icon="cash"
            onPress={() => setSelectedPaymentType('cash')}
            style={styles.paymentBtn}
          >
            Cash
          </Button>
          <Button
            testID="payment-debt"
            mode={selectedPaymentType === 'debt' ? 'contained' : 'outlined'}
            icon="credit-card-outline"
            onPress={() => setSelectedPaymentType('debt')}
            style={styles.paymentBtn}
          >
            Debt
          </Button>
        </View>
        <PaperTextInput
          testID="service-cost-input"
          mode="flat"
          label="Cost of delivering the service"
          value={serviceCost}
          onChangeText={setServiceCost}
          placeholder="0"
          keyboardType="numeric"
          left={<PaperTextInput.Icon icon={CurrencyIcon} />}
        />
        <Button
          testID="confirm-complete-button"
          mode="contained"
          icon="check-circle"
          disabled={!canConfirm}
          onPress={handleConfirm}
          style={styles.sheetBtn}
        >
          Confirm Complete
        </Button>
      </View>
    </BaseBottomSheet>
  );
};

const styles = StyleSheet.create({
  paymentContent: {
    padding: 16,
    gap: 16,
  },
  paymentLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 15,
  },
  paymentRow: {
    flexDirection: 'row',
    gap: 12,
  },
  paymentBtn: {
    flex: 1,
  },
  currencyIcon: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
  },
  sheetBtn: {
    marginTop: 4,
    borderRadius: 6,
  },
});

export default CompleteJobSheet;
